<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { signIn } from '../../core/firebase/auth.js'
import { ROUTES } from '../../core/config/constants.js'
import { useAuthStore } from '../../app/store/index.js'
import logoImg from '../../assets/logo/logo_300.png'
import MigrationNoticeModal from '../components/common/MigrationNoticeModal.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const email = ref('')
const password = ref('')
const loading = ref(false)
const error = ref('')
const showPassword = ref(false)

// Get redirect path from query
const redirectPath = computed(() => route.query.redirect || null)

/**
 * Wait for role to be available with timeout fallback
 * @returns {Promise<string|null>} The user role or null if timeout
 */
async function waitForRoleResolution() {
  const TIMEOUT_MS = 1500

  return new Promise((resolve) => {
    // If role is already available, resolve immediately
    if (authStore.userRole) {
      resolve(authStore.userRole)
      return
    }

    // If auth store is not loading anymore and no role, resolve with null
    if (!authStore.loading && authStore.isAuthResolved) {
      resolve(authStore.userRole)
      return
    }

    // Set up timeout fallback
    const timeout = setTimeout(() => {
      stopWatch()
      resolve(null)
    }, TIMEOUT_MS)

    // Watch for role changes
    const stopWatch = watch(
      () => ({ role: authStore.userRole, loading: authStore.loading }),
      ({ role, loading: isLoading }) => {
        // Role available
        if (role) {
          clearTimeout(timeout)
          stopWatch()
          resolve(role)
        }
        // Loading finished without role
        else if (!isLoading && authStore.isAuthResolved) {
          clearTimeout(timeout)
          stopWatch()
          resolve(null)
        }
      },
      { immediate: true }
    )
  })
}

async function handleLogin() {
  if (!email.value || !password.value) {
    error.value = 'נא למלא את כל השדות'
    return
  }

  loading.value = true
  error.value = ''

  try {
    await signIn(email.value, password.value)

    // If redirect param exists, use it (preserve existing behavior)
    if (redirectPath.value) {
      router.push(redirectPath.value)
      return
    }

    // Wait for role to resolve for role-based redirect
    const role = await waitForRoleResolution()

    // Redirect based on role
    if (role === 'admin' || role === 'agent') {
      router.push({ name: ROUTES.AGENTS_PORTAL })
    } else {
      router.push({ name: ROUTES.CATALOG })
    }
  } catch (err) {
    console.error('Login error:', err)
    // Map Firebase error codes to Hebrew messages
    switch (err.code) {
      case 'auth/invalid-email':
        error.value = 'כתובת אימייל לא תקינה'
        break
      case 'auth/user-disabled':
        error.value = 'חשבון זה הושבת'
        break
      case 'auth/user-not-found':
        error.value = 'משתמש לא נמצא'
        break
      case 'auth/wrong-password':
        error.value = 'סיסמה שגויה'
        break
      case 'auth/invalid-credential':
        error.value = 'פרטי התחברות שגויים'
        break
      case 'auth/too-many-requests':
        error.value = 'יותר מדי ניסיונות, נסה שוב מאוחר יותר'
        break
      default:
        error.value = 'ההתחברות נכשלה, נסה שוב'
    }
  } finally {
    loading.value = false
  }
}

function goHome() {
  router.push({ name: ROUTES.HOME })
}

function togglePassword() {
  showPassword.value = !showPassword.value
}
</script>

<template>
  <div class="login-page">
    <!-- Background decoration -->
    <div class="login-bg">
      <div class="login-bg-shape login-bg-shape-1"></div>
      <div class="login-bg-shape login-bg-shape-2"></div>
      <div class="login-bg-shape login-bg-shape-3"></div>
    </div>

    <div class="login-container">
      <!-- Logo & Branding -->
      <div class="login-brand" @click="goHome">
        <img :src="logoImg" alt="GoFood Logo" class="login-logo-img" />
      </div>

      <!-- Welcome Text -->
      <div class="login-welcome">
        <h2 class="login-title">ברוכים הבאים</h2>
        <p class="login-subtitle">התחבר לחשבון שלך כדי להמשיך</p>
      </div>

      <!-- Login Form -->
      <form class="login-form" @submit.prevent="handleLogin">
        <!-- Email Field -->
        <div class="input-group">
          <div class="input-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
              />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
          <input
            id="email"
            v-model="email"
            type="email"
            class="input-field"
            placeholder="כתובת אימייל"
            required
            autocomplete="email"
            :disabled="loading"
          />
        </div>

        <!-- Password Field -->
        <div class="input-group">
          <div class="input-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <input
            id="password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            class="input-field"
            placeholder="סיסמה"
            required
            autocomplete="current-password"
            :disabled="loading"
          />
          <button type="button" class="input-toggle" @click="togglePassword" tabindex="-1">
            <svg
              v-if="!showPassword"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
              />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          </button>
        </div>

        <!-- Error Message -->
        <Transition name="error">
          <div v-if="error" class="error-message">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{{ error }}</span>
          </div>
        </Transition>

        <!-- Submit Button -->
        <button
          type="submit"
          class="login-btn"
          :class="{ 'is-loading': loading }"
          :disabled="loading"
        >
          <span class="login-btn-text">{{ loading ? 'מתחבר...' : 'התחברות' }}</span>
          <div class="login-btn-icon">
            <svg
              v-if="!loading"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
            <div v-else class="login-btn-spinner"></div>
          </div>
        </button>
      </form>

      <!-- Footer -->
      <div class="login-footer">
        <p>אין לך חשבון? <a href="#">פנה למנהל המערכת</a></p>
      </div>

      <!-- Security Badge -->
      <div class="login-security">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        <span>חיבור מאובטח</span>
      </div>
    </div>

    <!-- Migration Notice (one-time, for users coming from the old system) -->
    <MigrationNoticeModal />
  </div>
</template>

<style scoped>
/* ============================================
   Page Container - Mobile First
   ============================================ */
.login-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  position: relative;
  overflow: hidden;
}

/* ============================================
   Background Decorations
   ============================================ */
.login-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.login-bg-shape {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
}

.login-bg-shape-1 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #283548, #334155);
  top: -100px;
  right: -100px;
  animation: float 8s ease-in-out infinite;
}

.login-bg-shape-2 {
  width: 250px;
  height: 250px;
  background: linear-gradient(135deg, #06b6d4, #3b82f6);
  bottom: -80px;
  left: -80px;
  animation: float 10s ease-in-out infinite reverse;
}

.login-bg-shape-3 {
  width: 150px;
  height: 150px;
  background: linear-gradient(135deg, #f59e0b, #ef4444);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 6s ease-in-out infinite;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-20px) scale(1.05);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.3;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 0.5;
    transform: translate(-50%, -50%) scale(1.1);
  }
}

/* ============================================
   Login Container - Glass Card
   ============================================ */
.login-container {
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  padding: 32px 24px;
  position: relative;
  z-index: 1;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

/* ============================================
   Brand / Logo
   ============================================ */
.login-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.login-brand:hover {
  transform: scale(1.02);
}

.login-logo-img {
  width: 120px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.3));
}

/* ============================================
   Welcome Text
   ============================================ */
.login-welcome {
  text-align: center;
  margin-bottom: 32px;
}

.login-title {
  font-size: 26px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px 0;
  letter-spacing: -0.3px;
}

.login-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
}

/* ============================================
   Form
   ============================================ */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ============================================
   Input Groups
   ============================================ */
.input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  right: 16px;
  width: 20px;
  height: 20px;
  color: rgba(255, 255, 255, 0.3);
  pointer-events: none;
  transition: color 0.2s ease;
}

.input-icon svg {
  width: 100%;
  height: 100%;
}

.input-field {
  width: 100%;
  height: 56px;
  padding: 0 48px 0 48px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  font-size: 16px;
  color: #fff;
  text-align: right;
  transition: all 0.2s ease;
  -webkit-appearance: none;
}

.input-field::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.input-field:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(40, 53, 72, 0.5);
  box-shadow: 0 0 0 4px rgba(40, 53, 72, 0.1);
}

.input-field:focus + .input-icon,
.input-group:focus-within .input-icon {
  color: #283548;
}

.input-field:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.input-toggle {
  position: absolute;
  left: 14px;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  padding: 0;
  color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: color 0.2s ease;
}

.input-toggle:hover {
  color: rgba(255, 255, 255, 0.6);
}

.input-toggle svg {
  width: 100%;
  height: 100%;
}

/* ============================================
   Error Message
   ============================================ */
.error-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 12px;
  color: #f87171;
  font-size: 14px;
}

.error-message svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.error-enter-active,
.error-leave-active {
  transition: all 0.3s ease;
}

.error-enter-from,
.error-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ============================================
   Login Button
   ============================================ */
.login-btn {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: linear-gradient(135deg, #283548 0%, #334155 100%);
  border: none;
  border-radius: 14px;
  font-size: 17px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 8px 24px rgba(40, 53, 72, 0.3);
  margin-top: 8px;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(40, 53, 72, 0.4);
}

.login-btn:active:not(:disabled) {
  transform: translateY(0);
}

.login-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.login-btn-text {
  transition: opacity 0.2s ease;
}

.login-btn-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-btn-icon svg {
  width: 100%;
  height: 100%;
  transform: rotate(180deg);
}

.login-btn-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ============================================
   Footer
   ============================================ */
.login-footer {
  text-align: center;
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.login-footer p {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
}

.login-footer a {
  color: #818cf8;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.login-footer a:hover {
  color: #475569;
}

/* ============================================
   Security Badge
   ============================================ */
.login-security {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 20px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.25);
}

.login-security svg {
  width: 14px;
  height: 14px;
}

/* ============================================
   Desktop Enhancements
   ============================================ */
@media (min-width: 480px) {
  .login-container {
    padding: 40px 36px;
  }

  .login-logo-img {
    width: 140px;
  }

  .login-title {
    font-size: 28px;
  }
}

@media (min-width: 768px) {
  .login-page {
    padding: 40px;
  }

  .login-container {
    padding: 48px 44px;
    border-radius: 28px;
  }

  .login-bg-shape-1 {
    width: 400px;
    height: 400px;
  }

  .login-bg-shape-2 {
    width: 350px;
    height: 350px;
  }

  .login-bg-shape-3 {
    width: 200px;
    height: 200px;
  }
}

/* ============================================
   Safe Area Padding (iOS notch)
   ============================================ */
@supports (padding: max(0px)) {
  .login-page {
    padding-top: max(24px, env(safe-area-inset-top));
    padding-bottom: max(24px, env(safe-area-inset-bottom));
    padding-left: max(16px, env(safe-area-inset-left));
    padding-right: max(16px, env(safe-area-inset-right));
  }
}

/* ============================================
   Reduced Motion
   ============================================ */
@media (prefers-reduced-motion: reduce) {
  .login-bg-shape {
    animation: none;
  }

  .login-btn,
  .input-field,
  .login-brand {
    transition: none;
  }
}
</style>
